import React from 'react';
import { makeStyles, Menu, MenuItem, Theme } from '@material-ui/core';
import { useHistory } from 'react-router';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import BarChart from '@material-ui/icons/BarChart';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import { MyUser, Role } from '../types/types';
import { StyledText } from './StyledText';
import Button from './Button';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: 40,
    width: 272,
    border: 'none',
    borderRadius: '100px',
    margin: '0 10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    background: theme.palette.primary.light,
    padding: '0 20px 0 0',
    '&:hover': {
      cursor: 'pointer'
    },
    '&:focus': {
      outline: 'none',
      borderColor: theme.palette.primary.main
    }
  },
  title: {
    flexGrow: 1,
    padding: '9px 17px',
    textAlign: 'left'
  },
  menuRoot: {
    minWidth: 272,
    padding: `0px ${theme.spacing(2)}px`,
    border: '1px solid #D0D6DA',
    borderRadius: '4px',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.25)'
  },
  email: {
    borderBottom: '1px solid #DAD9DF',
    '&:hover': {
      cursor: 'auto',
      backgroundColor: 'transparent'
    }
  },
  logout: {
    '&:hover': {
      cursor: 'auto',
      backgroundColor: 'transparent'
    }
  },
  logoutButton: {
    width: '100%',
    padding: 0,
    justifyContent: 'flex-start'
  }
}));

interface ProfileButtonProps {
  onLogout: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  goToProfile: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  user?: MyUser
}

const ProfileButton: React.FC<ProfileButtonProps> = ({
  onLogout,
  goToProfile,
  user
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const styles = useStyles();
  const history = useHistory();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onLogout(event);
    handleClose();
  };

  const handleGoToProfile = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToProfile(event);
    handleClose();
  };

  const handleGoToHome = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    history.push('/connector/activity');
    handleClose();
  };

  const arrowStyle: React.CSSProperties = anchorEl ? { transform: 'rotate(180deg)' } : {};

  const profileLabel = (user && user.role === Role.Ent) ? 'My company' : 'My profile';

  const getMenuItems = () => {
    if (user === undefined) {
      return [];
    }

    const { role } = user;
    const items = [
      <MenuItem className={styles.email} key={0}>
        {user.email}
      </MenuItem>,
      <MenuItem className={styles.logout} key={1}>
        <Button className={styles.logoutButton} variant='text' onClick={handleGoToProfile}>
          <AccountCircleIcon style={{ marginRight: '10px' }} />
          {profileLabel}
        </Button>
      </MenuItem>
    ];
    if (role === Role.Connector) {
      items.push(
        <MenuItem className={styles.logout} key={2}>
          <Button className={styles.logoutButton} variant='text' onClick={handleGoToHome}>
            <BarChart style={{ marginRight: '10px' }} />
            My activity
          </Button>
        </MenuItem>
      );
    }
    return items;
  };

  return (
    <div>
      <button type='button' className={styles.root} onClick={handleClick}>
        <StyledText className={styles.title} variant='body1' color='primary'>Account</StyledText>
        <KeyboardArrowDownIcon color='primary' style={arrowStyle} />
      </button>
      <Menu
        elevation={0}
        classes={{ paper: styles.menuRoot }}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 52,
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        id='customized-menu'
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        { getMenuItems() }
        <MenuItem className={styles.logout}>
          <Button className={styles.logoutButton} variant='text' onClick={handleLogout}>
            <ExitToAppIcon style={{ marginRight: '10px' }} />
            Log out
          </Button>
        </MenuItem>
      </Menu>
    </div>
  );
};

export default ProfileButton;
